import axios from 'axios';
import { firebaseService } from './firebase';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add Firebase token
api.interceptors.request.use(
  async (config) => {
    try {
      const token = await firebaseService.getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting Firebase token:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          // Unauthorized - redirect to login
          console.error('Unauthorized access');
          break;
        case 403:
          // Forbidden
          console.error('Forbidden access');
          break;
        case 429:
          // Rate limit exceeded
          console.error('Rate limit exceeded:', data.message);
          break;
        case 500:
          // Server error
          console.error('Server error');
          break;
        default:
          console.error('API Error:', data.message || error.message);
      }
    } else if (error.request) {
      // Network error
      console.error('Network error:', error.message);
    } else {
      // Other error
      console.error('Error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

class ApiService {
  // Authentication endpoints
  async getAuthStatus() {
    try {
      const response = await api.get('/api/auth/status/');
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async validateToken(token) {
    try {
      const response = await api.post('/api/auth/validate-token/', { token });
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async refreshUserStatus() {
    try {
      const response = await api.post('/api/auth/refresh-status/');
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  // User profile endpoints
  async getUserProfile() {
    try {
      const response = await api.get('/api/auth/profile/');
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async updateUserProfile(profileData) {
    try {
      const response = await api.patch('/api/auth/profile/', profileData);
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async getUserStats() {
    try {
      const response = await api.get('/api/auth/profile/stats/');
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  // Admin endpoints
  async getUserList() {
    try {
      const response = await api.get('/api/auth/users/');
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async updateUserType(userId, userType) {
    try {
      const response = await api.patch('/api/auth/users/update-type/', {
        user_id: userId,
        user_type: userType
      });
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async resetUserRequestCount(userId) {
    try {
      const response = await api.post('/api/auth/users/reset-count/', {
        user_id: userId
      });
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  // Usage endpoints
  async getApiUsage(page = 1, pageSize = 20) {
    try {
      const response = await api.get(`/api/auth/usage/?page=${page}&page_size=${pageSize}`);
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  // Utility endpoints
  async getUserTypes() {
    try {
      const response = await api.get('/api/auth/user-types/');
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }

  async healthCheck() {
    try {
      const response = await api.get('/api/auth/health/');
      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: error.response?.data || error.message };
    }
  }
}

export const apiService = new ApiService();
export { api };
export default ApiService;